"use client";

import * as React from "react";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onValueChange?: (value: string) => void;
  onChange?: (e: { target: { value: string } }) => void;
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  className?: string;
  required?: boolean;
}

function Select({
  value,
  onValueChange,
  onChange,
  options,
  placeholder = "Selecione...",
  label,
  className,
  required,
}: SelectProps) {
  const [open, setOpen] = React.useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={cn("space-y-2", className)} ref={selectRef}>
      {label && (
        <label className="text-sm font-medium text-foreground">{label}</label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex h-11 w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 text-sm transition-all duration-200",
            "focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20",
            !selectedOption && "text-foreground-secondary/50"
          )}
        >
          {selectedOption ? selectedOption.label : placeholder}
          <ChevronDown
            className={cn(
              "h-4 w-4 text-foreground-secondary transition-transform",
              open && "rotate-180"
            )}
            strokeWidth={1.5}
          />
        </button>

        {open && (
          <div className="absolute z-50 mt-2 w-full rounded-xl border border-gray-100 bg-white py-1 shadow-card">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (onValueChange) {
                    onValueChange(option.value);
                  }
                  if (onChange) {
                    onChange({ target: { value: option.value } });
                  }
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between px-4 py-2.5 text-sm transition-colors",
                  option.value === value
                    ? "bg-accent/5 text-accent"
                    : "text-foreground hover:bg-gray-50"
                )}
              >
                {option.label}
                {option.value === value && (
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { Select };
export type { SelectOption };
